import type { MaybeRefOrGetter } from 'vue'
import { DEFAULT_IDENTIFICATION_THEME } from '@/constants/identificationTheme'
import { parseThemeFontUrl, type ThemeFont } from '@/utils/themeFonts'

type ApiColors = NonNullable<IdentificationTheme['colors']>
type ApiColorKey = keyof ApiColors

const COLOR_VAR_MAP: Array<[ApiColorKey, string]> = [
  ['primary', '--v-theme-primary-xqes'],
  ['onPrimary', '--v-theme-on-primary-xqes'],
  ['onSurfaceHigh', '--v-theme-on-surface-high-xqes'],
  ['brandAccent', '--v-theme-brand-accent-xqes'],
  ['surfaceVariant', '--v-theme-surface-variant-xqes'],
  ['onSurfaceVariant', '--v-theme-on-surface-variant-xqes'],
  ['tertiary', '--v-theme-tertiary-xqes'],
]

const hexToRgbTriple = (hex: string): string | null => {
  let h = hex.replace('#', '')
  if (h.length === 3) {
    h = h
      .split('')
      .map(c => c + c)
      .join('')
  }
  if (h.length !== 6) return null
  const r = Number.parseInt(h.slice(0, 2), 16)
  const g = Number.parseInt(h.slice(2, 4), 16)
  const b = Number.parseInt(h.slice(4, 6), 16)
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
  return `${r}, ${g}, ${b}`
}

export default function useIdentificationTheme(invitingBusinessId: MaybeRefOrGetter<string>) {
  const { isDirectSign } = storeToRefs(useDirectSignStore())
  const { data: publicBusinessInfo, status } = usePublicInformation(invitingBusinessId, {
    watch: [() => toValue(invitingBusinessId)],
  })

  const theme = computed<ResolvedIdentificationTheme>(() => {
    const d = DEFAULT_IDENTIFICATION_THEME
    if (!isDirectSign.value) return d
    const api = publicBusinessInfo.value?.companyBranding?.theme
    if (!api) return d

    return {
      colors: api.colors,
      typography: { ...d.typography, ...api.typography },
      branding: { ...d.branding, ...api.branding },
    }
  })

  const FALLBACK_FONT_STACK = 'Averta, Arial, sans-serif'
  const headingFont = computed(() => parseThemeFontUrl(theme.value.typography.headingFontFamily))
  const bodyFont = computed(() => parseThemeFontUrl(theme.value.typography.bodyFontFamily))

  // A parsed theme font renders as "<Family>, Averta, Arial, sans-serif" so the browser falls
  // back to the default font if the theme font fails.
  const fontFamilyValue = (font: ThemeFont | null): string =>
    font ? `'${font.family}', ${FALLBACK_FONT_STACK}` : 'inherit'

  const rootStyle = computed<Record<string, string>>(() => {
    const style: Record<string, string> = {
      '--identification-body-font-xqes': fontFamilyValue(bodyFont.value),
      '--identification-heading-font-xqes': fontFamilyValue(headingFont.value),
    }
    const colors = theme.value.colors
    if (colors) {
      for (const [src, cssVar] of COLOR_VAR_MAP) {
        const value = colors[src]
        if (!value) continue
        const rgb = hexToRgbTriple(value)
        if (rgb) style[cssVar] = rgb
      }
    }
    return style
  })

  // <head> link descriptors for useHead
  const fontLinks = computed(() => {
    const urls = new Set<string>()
    if (headingFont.value) urls.add(headingFont.value.url)
    if (bodyFont.value) urls.add(bodyFont.value.url)
    if (urls.size === 0) return []

    return [
      { rel: 'preconnect', href: 'https://fonts.bunny.net', key: 'theme-font-preconnect' },
      {
        rel: 'preconnect',
        href: 'https://fonts.bunny.net',
        crossorigin: '' as const,
        key: 'theme-font-preconnect-crossorigin',
      },
      ...[...urls].map((url, index) => ({
        rel: 'stylesheet',
        href: url,
        key: `theme-font-css-${index}`,
      })),
    ]
  })

  const isThemeReady = ref(!isDirectSign.value)

  watch(
    [() => toValue(invitingBusinessId), status],
    ([id, s]) => {
      if (!id) return
      if (s === 'success' || s === 'error') isThemeReady.value = true
    },
    { immediate: true }
  )

  return { theme, publicBusinessInfo, rootStyle, isThemeReady, fontLinks }
}
